// Importaciones de React, librerías y recursos.
import React, { useState, useRef } from 'react';
import Swal from 'sweetalert2';
import cisoImage from '../img/cardd.png'; // Imagen para el servicio de CISO.
import ReCAPTCHA from "react-google-recaptcha";

/**
 * Componente FormularioServicioCISO.
 * Renderiza un formulario específico para solicitar información sobre el Servicio de CISO.
 * La estructura y lógica son idénticas a las de los otros formularios de servicios.
 */
const FormularioServicioCISO = () => {
  // Estado para todos los campos del formulario.
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    correo: '',
    puesto: '',
    telefono: '',
    dudasComentarios: '',
    coordinarLlamada: '',
    region: 'MX',
    fechaContacto: '',
    horaContacto: ''
  });

  // Referencia para el componente reCAPTCHA.
  const recaptchaRef = useRef();

  // Manejador genérico para actualizar el estado del formulario.
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // --- FUNCIONES DE VALIDACIÓN ---

  const validatePhoneNumber = (phone) => /^\d{10,15}$/.test(phone);

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    const invalidProviders = ['gmail', 'yahoo', 'outlook', 'hotmail'];
    const domain = email.split('@')[1]?.split('.')[0];
    return emailRegex.test(email) && !invalidProviders.includes(domain);
  };

  // --- MANEJADOR DE ENVÍO DEL FORMULARIO ---
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // 1. Validaciones previas al envío.
    const recaptchaValue = recaptchaRef.current.getValue();
    if (!recaptchaValue) {
      Swal.fire('Captcha no completado', 'Por favor, completa el captcha.', 'warning');
      return;
    }
    if (!validatePhoneNumber(formData.telefono)) {
      Swal.fire('Número de teléfono no válido', 'Por favor, ingresa un número válido.', 'warning');
      return;
    }
    if (!validateEmail(formData.correo)) {
      Swal.fire('Correo no válido', 'Te aconsejamos usar un correo electrónico de empresa.', 'warning');
      return;
    }

    // 2. Envío de datos al webhook.
    try {
      const formDataToSend = {
        ...formData,
        producto: 'Servicio CISO', // Identificador específico de este formulario.
        recaptchaValue,
        fechaContacto: new Date().toLocaleDateString(),
        horaContacto: new Date().toLocaleTimeString()
      };

      const response = await fetch('https://n8n.srv871810.hstgr.cloud/webhook/7c6029cc-172c-435c-9f7f-c484793b738b', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formDataToSend)
      });

      if (!response.ok) {
        throw new Error('Error al enviar el formulario');
      }

      // 3. Notificación de éxito y limpieza del formulario.
      Swal.fire('Enviado', 'El formulario se ha enviado con éxito.', 'success');
      setFormData({
        nombre: '', apellido: '', correo: '', puesto: '', telefono: '',
        dudasComentarios: '', coordinarLlamada: '', region: formData.region,
        fechaContacto: '', horaContacto: ''
      });
      recaptchaRef.current.reset();

    } catch (error) {
      Swal.fire('Error', error.message || 'Hubo un error al enviar el formulario.', 'error');
    }
  };

  return (
    <div className="flex flex-col md:flex-row bg-blue-500">
      {/* Columna izquierda: Imagen. */}
      <div className="md:w-1/2 flex items-center p-5">
        <img src={cisoImage} alt="Servicio CISO" className="w-full rounded-2xl" />
      </div>
      {/* Columna derecha: Formulario. */}
      <div className="md:w-1/2 p-6 fondoFormulario">
        <h2 className="text-3xl font-bold mb-4 text-center text-white mt-10">Formulario para Servicio CISO</h2>
        <form onSubmit={handleFormSubmit} className='bg-blue-400 rounded-2xl p-5'>
          <input type="hidden" name="producto" value="Servicio CISO" />
          <div className="mb-4">
            <label htmlFor="nombre" className="block mb-2 text-white">Nombre:</label>
            <input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} className="border rounded-md px-2 py-1 w-full" required />
          </div>
          <div className="mb-4">
            <label htmlFor="apellido" className="block mb-2 text-white">Apellido:</label>
            <input type="text" id="apellido" name="apellido" value={formData.apellido} onChange={handleChange} className="border rounded-md px-2 py-1 w-full" required />
          </div>
          <div className="mb-4">
            <label htmlFor="correo" className="block mb-2 text-white">Correo Electrónico:</label>
            <input type="email" id="correo" name="correo" value={formData.correo} onChange={handleChange} className="border rounded-md px-2 py-1 w-full" required pattern="[a-zA-Z0-9._%+-]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}" title="Por favor, ingresa un correo electrónico válido de empresa." />
          </div>
          <div className="mb-4">
            <label htmlFor="puesto" className="block mb-2 text-white">Puesto:</label>
            <input type="text" id="puesto" name="puesto" value={formData.puesto} onChange={handleChange} className="border rounded-md px-2 py-1 w-full" required />
          </div>
          <div className="mb-4">
            <label htmlFor="telefono" className="block mb-2 text-white">Teléfono:</label>
            <select id="region" name="region" value={formData.region} onChange={handleChange} className="border rounded-md px-2 py-1 w-full mb-2">
              <option value="MX">México (+52)</option>
              <option value="AR">Argentina (+54)</option>
              <option value="US">Estados Unidos (+1)</option>
              <option value="CA">Canadá (+1)</option>
              <option value="GB">Reino Unido (+44)</option>
              <option value="AU">Australia (+61)</option>
              <option value="DE">Alemania (+49)</option>
              <option value="FR">Francia (+33)</option>
              <option value="ES">España (+34)</option>
              <option value="VE">Venezuela (+58)</option>
            </select>
            <input
              type="tel"
              id="telefono"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              className="border rounded-md px-2 py-1 w-full"
              required
              maxLength={formData.region === 'MX' ? 10 : 15}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="dudasComentarios" className="block mb-2 text-white">Dudas & Comentarios:</label>
            <textarea id="dudasComentarios" name="dudasComentarios" value={formData.dudasComentarios} onChange={handleChange} className="border rounded-md px-2 py-1 w-full h-32"></textarea>
          </div>
          <div className="mb-4">
            <p className="text-white">¿Te gustaría coordinar una llamada con un especialista para profundizar en alguno de estos temas?</p>
            <div className="flex items-center">
              <input type="radio" id="coordinarLlamadaSi" name="coordinarLlamada" value="si" onChange={handleChange} className="mr-2" />
              <label htmlFor="coordinarLlamadaSi" className="text-white mr-6">Sí</label>
              <input type="radio" id="coordinarLlamadaNo" name="coordinarLlamada" value="no" onChange={handleChange} className="mr-2" />
              <label htmlFor="coordinarLlamadaNo" className="text-white">No</label>
            </div>
          </div>
          <div className='flex justify-center m-2'>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6Le3lb4pAAAAAIHJ49iYIgs-O4rowqUYrFEkbLVx"
            />
          </div>
          <button type="submit" className="bg-blue-500 text-white py-2 px-4 rounded-md border-transparent hover:bg-blue-600 transition-colors duration-300 w-full">Enviar</button>
        </form>
      </div>
    </div>
  );
};

export default FormularioServicioCISO;
